import { ReviewEntity } from '@/entities/review.entity';
import Modal from '@/ui/Modal';
import { ChatContextProvider } from '@/context/chat.context';
import Chat from '@/components/Chat';
import ModalAiResponseData from '@/components/ModalAiResponseData';
import { OpenedPhotos, OpenedReview } from '@/components/feedbacks-table/use-feedbacks-table-modals';

export type FeedbacksTableModalsProps = {
    openedReview: OpenedReview | null;
    setOpenedReview: (value: OpenedReview | null) => void;
    openedAiResponseReview: ReviewEntity | null;
    setOpenedAiResponseReview: (value: ReviewEntity | null) => void;
    openedPhotos: OpenedPhotos | null;
    isPhotoLoading: boolean;
    setIsPhotoLoading: (value: boolean) => void;
    onPhotoPreviewClose: () => void;
    onPreviousPhoto: () => void;
    onNextPhoto: () => void;
};

export const FeedbacksTableModals = ({
    openedReview,
    setOpenedReview,
    openedAiResponseReview,
    setOpenedAiResponseReview,
    openedPhotos,
    isPhotoLoading,
    setIsPhotoLoading,
    onPhotoPreviewClose,
    onPreviousPhoto,
    onNextPhoto,
}: FeedbacksTableModalsProps) => {
    const openedPhoto = openedPhotos?.photos[openedPhotos.index];
    const openedReviewProductSku = openedReview?.review.product.sku.trim() || '';
    const openedReviewProductName = openedReview?.review.product.name.trim() || '';

    return (
        <>
            {openedReview && (
                <Modal isShown={true} setIsShown={() => setOpenedReview(null)} title="Диалог" closable>
                    <div className="p-2 w-full h-full flex min-h-0 flex-col gap-2">
                        <p className="shrink-0 px-2 pt-1 text-sm break-words">
                            <span>Продукт: </span>
                            {openedReviewProductSku && (
                                <span className="text-xs text-black/50">{openedReviewProductSku}</span>
                            )}
                            {openedReviewProductSku && openedReviewProductName && ' '}
                            {openedReviewProductName}
                        </p>
                        <ChatContextProvider
                            review={openedReview.review}
                            history={openedReview.history}
                            pendingAnswerDraft={openedReview.pendingAnswerDraft}
                        >
                            <div className="min-h-0 flex-1">
                                <Chat onMessageSubmitted={() => setOpenedReview(null)} />
                            </div>
                        </ChatContextProvider>
                    </div>
                </Modal>
            )}
            {openedAiResponseReview?.aiApiResponse && (
                <ModalAiResponseData
                    isShown={true}
                    setIsShown={() => setOpenedAiResponseReview(null)}
                    response={openedAiResponseReview.aiApiResponse}
                />
            )}
            {openedPhotos && (
                <Modal
                    isShown={true}
                    setIsShown={onPhotoPreviewClose}
                    hideHeader
                    className="h-[90vh] w-[90vw] max-w-[1000px]"
                >
                    <div className="relative h-full w-full bg-slate-900 p-4">
                        <button
                            type="button"
                            aria-label="Закрыть просмотр фото"
                            className="absolute right-4 top-4 z-10 h-8 w-8 rounded-full bg-white/90 text-sm text-slate-900 transition-colors hover:bg-white"
                            onClick={onPhotoPreviewClose}
                        >
                            x
                        </button>
                        <div className="flex h-full items-center justify-center gap-4">
                            <button
                                type="button"
                                aria-label="Предыдущее фото"
                                className="h-10 w-10 rounded-full bg-white/90 text-xl text-slate-900 transition-colors hover:bg-white disabled:cursor-default disabled:bg-white/40"
                                onClick={onPreviousPhoto}
                                disabled={openedPhotos.photos.length < 2}
                            >
                                {'<'}
                            </button>
                            <div className="relative flex h-full w-full items-center justify-center">
                                {isPhotoLoading && (
                                    <div className="absolute inset-0 flex items-center justify-center rounded bg-slate-800/70 text-sm text-slate-200">
                                        Загрузка фото...
                                    </div>
                                )}
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    key={`${openedPhotos.index}-${openedPhoto?.fullSize || ''}`}
                                    src={openedPhoto?.fullSize || ''}
                                    alt={`Фото ${openedPhotos.index + 1}`}
                                    className={`max-h-full max-w-full object-contain transition-opacity duration-150 ${
                                        isPhotoLoading ? 'opacity-0' : 'opacity-100'
                                    }`}
                                    onLoad={() => setIsPhotoLoading(false)}
                                    onError={() => setIsPhotoLoading(false)}
                                />
                            </div>
                            <button
                                type="button"
                                aria-label="Следующее фото"
                                className="h-10 w-10 rounded-full bg-white/90 text-xl text-slate-900 transition-colors hover:bg-white disabled:cursor-default disabled:bg-white/40"
                                onClick={onNextPhoto}
                                disabled={openedPhotos.photos.length < 2}
                            >
                                {'>'}
                            </button>
                        </div>
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded bg-black/70 px-3 py-1 text-sm text-white">
                            {openedPhotos.index + 1} / {openedPhotos.photos.length}
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};
